import {
  AnimalCard,
  AnimalContext,
  ExtensionType,
} from '../types/ExtensionCard';
import { sharedExtensionCards } from '../gameData/extensions.shared';
import { getWorkingExtensions } from './getWorkingExtensions';

type CanAttackResultPositive = {
  intellectUsed?: number;
  canAttack: true;
};

type CanAttackResultNegative = {
  canAttack: false;
  reasons: string[];
};

type CanAttackResult = CanAttackResultPositive | CanAttackResultNegative;

export const canAttack = (
  predator: AnimalCard,
  preyContext: AnimalContext,
): CanAttackResult => {
  if (
    !getWorkingExtensions(predator).some(
      (extension) => extension.type === ExtensionType.predator,
    )
  ) {
    return {
      canAttack: false,
      reasons: ['The attacker must be a predator'],
    };
  }

  if (predator === preyContext.animal) {
    return {
      canAttack: false,
      reasons: ['You cannot attack yourself'],
    };
  }

  if (predator.cantEat) {
    return {
      canAttack: false,
      reasons: ['The predator cannot eat'],
    };
  }

  if (predator.isBlocked) {
    return {
      canAttack: false,
      reasons: ['The predator cannot attack until the end of the turn'],
    };
  }

  if (
    predator.extensions.some(
      (extension) => sharedExtensionCards[extension.type].isBlockingEverything,
    )
  ) {
    return {
      canAttack: false,
      reasons: ['The predator is still an egg'],
    };
  }

  if (
    preyContext.animal.extensions.some(
      (extension) => sharedExtensionCards[extension.type].isBlockingEverything,
    )
  ) {
    return {
      canAttack: true,
    };
  }

  let intellectUsed = 0;
  let intellectCount = getWorkingExtensions(predator).filter(
    (extension) =>
      extension.type === ExtensionType.intellect &&
      !extension.wasUsedInCurrentStage,
  ).length;

  const ignoreWithIntellect = () => {
    intellectCount--;
    intellectUsed++;
    if (intellectCount >= 0) {
      return true;
    }
    return false;
  };

  if (preyContext.animal.isHidden && !ignoreWithIntellect()) {
    return {
      canAttack: false,
      reasons: ['The animal is hidden'],
    };
  }

  if (preyContext.animal.patronsCount > 0 && !ignoreWithIntellect()) {
    return {
      canAttack: false,
      reasons: ['The animal is protected'],
    };
  }

  const canAttackResults = getWorkingExtensions(predator).map(
    (extension): CanAttackResult => {
      const extensionData = sharedExtensionCards[extension.type];
      if (!extensionData.canAttack) {
        return {
          canAttack: true,
        };
      }
      const localCanAttack = extensionData.canAttack(
        predator,
        preyContext.animal,
      );
      return {
        canAttack: localCanAttack,
        reasons: [extensionData.meta.description],
      };
    },
  );

  const defenceExtensionsResults = getWorkingExtensions(preyContext.animal).map(
    (extension): CanAttackResult => {
      const extensionData = sharedExtensionCards[extension.type];
      if (!extensionData.canBeAttacked) {
        return {
          canAttack: true,
        };
      }
      const localCanAttack = extensionData.canBeAttacked(preyContext, predator);
      return {
        canAttack: localCanAttack,
        reasons: [extensionData.meta.description],
      };
    },
  );

  const defenceResultsBlockedAttack = [
    ...canAttackResults,
    ...defenceExtensionsResults,
  ].filter(
    (result) => !result.canAttack && !ignoreWithIntellect(),
  ) as CanAttackResultNegative[];

  if (defenceResultsBlockedAttack.length === 0) {
    return {
      intellectUsed,
      canAttack: true,
    };
  }

  return {
    canAttack: false,
    reasons: defenceResultsBlockedAttack
      .flatMap((result) => result.reasons)
      .filter(Boolean),
  };
};
